import { Card } from '@120wateraudit/envirio-components'
import React, { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDebounce } from 'react-use'
import { Search, SearchResultData } from 'semantic-ui-react'
import styled from 'styled'
import ReactGA from 'react-ga4'
import DisclaimerModal from 'components/DisclaimerModal'
import SearchResult from 'components/SearchResult'
import ServiceLineMap from 'components/ServiceLineMap'
import {
  AssetItem,
  AssetMapCluster,
  useAssetLocationSearchQuery
} from 'generated/graphql'
import useAccount from 'hooks/useAccount'
import useMap from 'hooks/useMap'
import useMapBounds from 'hooks/useMapBounds'
import useMapCenter from 'hooks/useMapCenter'
import useMapData from 'hooks/useMapData'
import LayoutMain from 'layouts/LayoutMain'

interface MapData {
  clusters?: AssetMapCluster[]
  points?: AssetItem[]
}

const PAGE_SIZE = 5

const PageAccount = () => {
  const { map, onLoad, onUnmount } = useMap()
  const { bounds, updateBounds } = useMapBounds(map)
  const { clusters, points } = useMapData(bounds) as MapData
  const { center } = useMapCenter()
  const history = useHistory()
  const [search, setSearch] = useState<string>('')
  const [open, setOpen] = React.useState<boolean>(false)
  const [value, setValue] = React.useState<string>()
  const [assetOpen, setAssetOpen] = useState<string>('')

  const { accountId, hideServiceLines } = useAccount({
    onCompleted(data) {
      if (data?.accountDetail) {
        return
      }

      history.replace('/')
    }
  })

  const {
    data: assetsData,
    loading: assetsLoading
  } = useAssetLocationSearchQuery({
    skip: !accountId || !search || search.trim().length < 3,
    variables: { accountId, resultsCount: PAGE_SIZE, search }
  })

  const resultsData = (assetsData?.assetLocationSearch?.items || []).map(a => {
    return {
      // Title and key need to be given otherwise Semantic UI complains. These are both required and passed as props into each SearchResult component.
      assetid: a?.id,
      key: a?.id,
      location: a?.location,
      title: a?.location?.addressLine1
    }
  })

  useDebounce(
    () => {
      setSearch(value ?? '')
    },
    250,
    [value]
  )

  const handleResultSelect = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    data: SearchResultData
  ) => {
    ReactGA.event({
      action: `Select Search Result`,
      category: 'Search'
    })
    const { assetid, location } = data.result || {}
    const { latitude, longitude } = location || {}

    const position: google.maps.LatLngLiteral = {
      lat: latitude ?? 0,
      lng: longitude ?? 0
    }

    const zoomLevel = map?.getZoom()

    if (position) {
      map?.panTo(position)
    }

    if ((zoomLevel || zoomLevel === 0) && zoomLevel < 19) {
      map?.setZoom(19)
    }
    setAssetOpen(assetid)
  }

  const handleSearchChange = useCallback((e, data) => {
    setValue(data.value)
  }, [])

  if (!accountId) {
    return null
  }

  let mapTitle = 'Find Service Lines or Samples'
  if (hideServiceLines) {
    mapTitle = 'Find Samples by Address'
  }
  return (
    <LayoutMain>
      <DisclaimerModal open={open} setOpen={setOpen} />
      <MapContainer>
        <MapTitle>
          <h4>{mapTitle}</h4>
          <Search
            fluid
            loading={assetsLoading}
            noResultsMessage="No location found."
            onResultSelect={handleResultSelect}
            onSearchChange={handleSearchChange}
            placeholder="Search for an address"
            resultRenderer={SearchResult}
            results={resultsData}
            value={value}
          />
        </MapTitle>
        <ServiceLineMap
          assetOpen={assetOpen}
          bounds={bounds}
          clusters={clusters || []}
          mapCenter={center}
          onLoad={onLoad}
          onUnmount={onUnmount}
          points={points || []}
          setAssetOpen={setAssetOpen}
          updateBounds={updateBounds}
        />
      </MapContainer>
    </LayoutMain>
  )
}

const MapContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  text-align: center;
`

const MapTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  & > h4 {
    flex: 2;
    text-align: start;
  }
  & > .search {
    height: fit-content;
    flex: 1;
    & > .input {
      width: 100%;
      float: right;
    }
  }

  .ui.search .prompt {
    border: 1px solid #707683;
  }
`

export default PageAccount
